import { ArrowDownward, ArrowUpward, Check } from '@mui/icons-material'
import { Box, Button, Stack } from '@dtx-company/design-system/src'
import { FC } from 'react'
import { LoggedOutGeneratorSection } from '../LoggedOutCodeGenerator.types'
import {
  selectDesignFirst,
  selectFirstSectionIsActive,
  selectSecondSectionIsActive,
  selectSection
} from '@app/code/src/machines/locg/locgSelectors'
import { useGlobalServices } from '@app/common/src/hooks/useGlobalServices'
import { useSelector } from '@xstate/react'
import useTranslation from 'next-translate/useTranslation'

export const FormActionButtons: FC = () => {
  const { t } = useTranslation('logged-out-generator')
  const { locgService } = useGlobalServices()
  const designFirst = useSelector(locgService, selectDesignFirst)
  const currentSection = useSelector(locgService, selectSection)
  const firstSectionIsActive = useSelector(locgService, selectFirstSectionIsActive)
  const secondSectionIsActive = useSelector(locgService, selectSecondSectionIsActive)
  const isValidating = useSelector(locgService, state =>
    state.matches('scanDestination.validating')
  )

  const leftButtonProps = firstSectionIsActive
    ? {
        color: 'primary' as const,
        variant: 'contained' as const,
        startIcon: designFirst ? Check : ArrowDownward,
        onClick: () => {
          designFirst
            ? locgService.send({ type: 'BACK', target: LoggedOutGeneratorSection.ScanDestination })
            : locgService.send({ type: 'NEXT', target: LoggedOutGeneratorSection.CodeDesign })
        },
        loading: isValidating,
        children: (
          <span>
            {designFirst
              ? t('LoggedOutGenerator.FormActionButtons.LooksGood')
              : t('LoggedOutGenerator.FormActionButtons.ContinueToDesign')}
          </span>
        )
      }
    : {
        color: 'secondary' as const,
        variant: 'outlined' as const,
        startIcon: ArrowUpward,
        onClick: () => {
          designFirst
            ? locgService.send({ type: LoggedOutGeneratorSection.CodeDesign })
            : locgService.send({ type: 'BACK', target: LoggedOutGeneratorSection.ScanDestination })
        },
        children: (
          <span>
            {designFirst
              ? t('LoggedOutGenerator.FormActionButtons.CodeDesign')
              : t('LoggedOutGenerator.FormActionButtons.ScanDestination')}
          </span>
        )
      }

  return (
    <Stack
      gap={{ xs: 2, md: 4 }}
      sx={theme => ({
        position: {
          xs: 'fixed',
          md: 'absolute'
        },
        bottom: 0,
        left: 0,
        width: '100%',
        background: `linear-gradient(rgba(255, 255, 255, 0), ${theme.palette.surface[0]} 20%)`,
        px: { xs: 4.75, md: 8 },
        py: { xs: 6, md: 10 },
        zIndex: 5,
        justifyContent: { xs: 'center', md: 'left' }
      })}
      direction="row"
    >
      <Box
        sx={theme => ({
          display: {
            xs: 'inline-flex',
            md: firstSectionIsActive ? 'inline-flex' : 'none'
          },
          flex: {
            xs: 1,
            md: 'initial'
          },
          minWidth: 'fit-content',
          button: {
            px: {
              xs: 3.5,
              md: 4
            },
            py: {
              xs: 2.5,
              md: 3
            },
            fontSize: {
              xs: theme.spacing(3.5),
              md: theme.spacing(4)
            },
            width: {
              xs: '100%',
              md: 'auto'
            }
          }
        })}
      >
        <Button
          size="large"
          {...leftButtonProps}
          data-heap-id="LoggedOutGenerator_FormActionButtons_NavigationButton"
          data-section={currentSection}
          data-action={firstSectionIsActive ? 'ContinueToDesign' : 'BackToScanDestination'}
        />
      </Box>

      <Box
        sx={theme => ({
          display: designFirst && firstSectionIsActive ? 'none' : 'inline-flex',
          flexShrink: 0,
          flex: {
            xs: 1,
            md: 'initial'
          },
          button: {
            px: {
              xs: 3.25,
              md: 3.75
            },
            py: {
              xs: 2.25,
              md: 3
            },
            fontSize: {
              xs: theme.spacing(3.5),
              md: theme.spacing(4)
            },
            width: {
              xs: '100%',
              md: 'auto'
            }
          }
        })}
      >
        <Button
          size="large"
          color={secondSectionIsActive ? 'primary' : 'secondary'}
          variant={secondSectionIsActive ? 'contained' : 'outlined'}
          onClick={() =>
            locgService.send({
              type: 'DOWNLOAD_CODE',
              target: LoggedOutGeneratorSection.SignUp
            })
          }
          data-heap-id="LoggedOutGenerator_FormActionButtons_DownloadCodeButton"
          data-section={currentSection}
        >
          {t('LoggedOutGenerator.FormActionButtons.DownloadCode')}
        </Button>
      </Box>
    </Stack>
  )
}
